import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useApp } from "@/context/AppProviders";

const Hero = () => {
  const { t } = useApp();

  const scrollTo = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-24 sm:pt-40 sm:pb-32"
    >
      {/* Background image + overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByb29tJTIwZGFyayUyMGJsdWV8ZW58MHx8fHwxNzgwNTkxNTkzfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="h-full w-full object-cover opacity-30 dark:opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/90 to-background" />
        <div className="absolute inset-0 grid-texture radial-fade opacity-50" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-sm border border-blue-600/40 bg-blue-600/10 px-3 py-1.5 text-xs font-mono font-medium text-blue-500"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
            </span>
            {t.hero.badge}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            data-testid="hero-title"
            className="font-heading mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]"
          >
            {t.hero.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            data-testid="hero-subtitle"
            className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-muted-foreground"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <button
              onClick={() => scrollTo("#pricing")}
              data-testid="hero-primary-cta"
              className="group inline-flex h-12 items-center justify-center gap-2 rounded-sm bg-blue-600 px-7 text-sm font-medium text-white hover:bg-blue-500 transition-colors duration-200"
            >
              {t.hero.ctaPackages}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollTo("#contact")}
              data-testid="hero-secondary-cta"
              className="inline-flex h-12 items-center justify-center rounded-sm border border-border bg-card/50 px-7 text-sm font-medium hover:bg-secondary transition-colors duration-200"
            >
              {t.hero.ctaDemo}
            </button>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-3"
            data-testid="hero-trust"
          >
            {t.hero.trust.map((item) => (
              <li
                key={item}
                className="flex items-center gap-2 text-sm text-muted-foreground"
              >
                <CheckCircle2 className="h-4 w-4 text-blue-500" />
                {item}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.55 }}
            data-testid="hero-stats"
            className="mt-12 grid max-w-2xl grid-cols-2 gap-px overflow-hidden rounded-sm border border-border bg-border sm:grid-cols-4"
          >
            {t.hero.stats.map((stat) => (
              <div key={stat.label} className="bg-background/60 p-4 text-center">
                <p className="font-heading text-2xl font-bold tracking-tight text-blue-500">
                  {stat.value}
                </p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  {stat.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
